import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { HiKey } from 'react-icons/hi';
import { AppPageShell, Button, PageHeader, Panel } from './common/ui';
import ThemeSegmentedControl from './ThemeSegmentedControl';

const Settings = () => {
  const { user } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    
    try {
      await api.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      toast.success('Password changed successfully');
      
      // Reset form
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      toast.error(error.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  return (
    <AppPageShell maxWidthClass="max-w-4xl">
      <PageHeader
        eyebrow="Account"
        title="Account Settings"
        description="Manage profile details, appearance, and credential changes for this workspace."
      />

      <Panel className="mb-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900 dark:text-slate-100">
              Appearance
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
              Choose a light or dark workspace, or match your device setting.
            </p>
          </div>
          <ThemeSegmentedControl className="shrink-0 w-full sm:w-auto justify-center" />
        </div>
      </Panel>
      
      <Panel className="mb-6">
          <h2 className="text-lg font-medium text-gray-900 dark:text-slate-100">Account Information</h2>
          <div className="mt-4 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Email</label>
              <div className="mt-1 app-readonly-field">
                {user?.email}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">Role</label>
              <div className="mt-1 app-readonly-field">
                {user?.isAdmin ? 'Administrator' : 'User'}
              </div>
            </div>
          </div>
      </Panel>
      
      <Panel>
          <h2 className="text-lg font-medium text-gray-900 dark:text-slate-100 flex items-center">
            <HiKey className="h-5 w-5 text-gray-500 dark:text-slate-400 mr-2" />
            Change Password
          </h2>
          <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
            <div>
              <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                Current Password
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                required
                className="mt-1 block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                value={passwordForm.currentPassword}
                onChange={handleInputChange}
              />
            </div>
            
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                required
                className="mt-1 block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                value={passwordForm.newPassword}
                onChange={handleInputChange}
              />
            </div>
            
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="mt-1 block w-full app-field py-2 px-3 focus:outline-none sm:text-sm"
                value={passwordForm.confirmPassword}
                onChange={handleInputChange}
              />
            </div>
            
            <div className="pt-4">
              <Button
                type="submit"
                disabled={loading}
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </div>
          </form>
      </Panel>
    </AppPageShell>
  );
};

export default Settings; 
